import { ComponentType, type Component } from "@/types";
import type { HAEntity } from "@/services/api/haApi";

export type HomeAssistantBindingRiskLevel = "low" | "review_required";

export interface HomeAssistantEntitySelection {
  instanceId?: string;
  instanceName?: string;
  entityId: string;
  domain?: string;
  displayName?: string;
  areaName?: string;
  state?: string;
  attribute?: string;
}

export interface HomeAssistantBindingMetadata {
  source: "home-assistant";
  type: "entity-state";
  version: 2;
  entityId: string;
  domain: string;
  attribute: string;
  instanceId?: string;
  instanceName?: string;
  displayName?: string;
  areaName?: string;
  selectedVia: "entity-picker";
  manualReviewRequired: boolean;
}

export interface HomeAssistantDataSourceMetadata {
  type: "home-assistant";
  mode: "ha-entity-state";
  entityId: string;
  domain: string;
  attribute: string;
  instanceId?: string;
  instanceName?: string;
}

export interface HomeAssistantManualActionMetadata {
  type: "home-assistant-placeholder";
  manualOnly: true;
  entityId: string;
  domain: string;
  service: string;
  serviceLabel: string;
  serviceData?: Record<string, string | number | boolean>;
  riskLevel: HomeAssistantBindingRiskLevel;
  reviewRequired: boolean;
  safetyNote: string;
}

export interface HomeAssistantBindingUpdate {
  properties: Record<string, unknown>;
}

interface InferredHomeAssistantServiceSpec {
  service: string;
  serviceData?: Record<string, string | number | boolean>;
}

const CONTROL_COMPONENT_TYPES = new Set<ComponentType>([
  ComponentType.ICON_BUTTON,
  ComponentType.PUSH_BUTTON,
  ComponentType.TOGGLE_SWITCH,
  ComponentType.RADIAL_DIAL,
  ComponentType.SLIDER,
  ComponentType.STEPPER,
  ComponentType.SEGMENTED_CONTROL,
  ComponentType.COLOR_PICKER,
  ComponentType.THERMOSTAT,
]);

const DATA_BINDABLE_COMPONENT_TYPES = new Set<ComponentType>([
  ComponentType.TEXT,
  ComponentType.DATA_TEXT,
  ComponentType.DATA_CARD,
  ComponentType.STATUS_LIGHT,
  ComponentType.LEVEL_INDICATOR,
  ComponentType.GAUGE_CHART,
  ComponentType.VERTICAL_GAUGE_CHART,
  ComponentType.CHART,
  ComponentType.BAR_CHART,
  ComponentType.LINE_CHART,
  ComponentType.AREA_CHART,
  ComponentType.PIE_CHART,
  ComponentType.SCATTER_CHART,
  ComponentType.HEATMAP_CHART,
  ComponentType.TREEMAP_CHART,
  ComponentType.TABLE,
  ComponentType.ADVANCED_TABLE,
  ComponentType.MAP_CHART,
]);

const REVIEW_REQUIRED_DOMAINS = new Set([
  "alarm_control_panel",
  "climate",
  "cover",
  "fan",
  "humidifier",
  "input_boolean",
  "lock",
  "remote",
  "siren",
  "valve",
  "water_heater",
]);

const REVIEW_REQUIRED_ENTITY_WORDS = /(^|[._\-\s])(garage|gate|door|lock|alarm|security|camera|hvac|thermostat|irrigation|sprinkler|valve|water|pump|power|outlet|breaker|mqtt|http|shell|ssh)([._\-\s]|$)/i;

const HOME_ASSISTANT_CONTROL_ALLOWLIST = {
  button: ["press"],
  climate: ["set_hvac_mode", "set_temperature", "turn_off", "turn_on"],
  cover: ["close_cover", "open_cover", "set_cover_position", "stop_cover"],
  fan: ["oscillate", "set_percentage", "turn_off", "turn_on"],
  input_boolean: ["toggle", "turn_off", "turn_on"],
  input_number: ["set_value"],
  light: ["toggle", "turn_off", "turn_on"],
  lock: ["lock", "open", "unlock"],
  media_player: [
    "media_pause",
    "media_play",
    "media_stop",
    "toggle",
    "turn_off",
    "turn_on",
    "volume_mute",
    "volume_set",
  ],
  number: ["set_value"],
  select: ["select_option"],
  switch: ["toggle", "turn_off", "turn_on"],
  vacuum: ["pause", "return_to_base", "start", "stop"],
} as const satisfies Record<string, readonly string[]>;

type HomeAssistantControlDomain = keyof typeof HOME_ASSISTANT_CONTROL_ALLOWLIST;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isHomeAssistantControlDomain = (
  domain: string,
): domain is HomeAssistantControlDomain =>
  Object.prototype.hasOwnProperty.call(HOME_ASSISTANT_CONTROL_ALLOWLIST, domain);

export function supportsHomeAssistantBinding(componentType: ComponentType): boolean {
  return (
    CONTROL_COMPONENT_TYPES.has(componentType) ||
    DATA_BINDABLE_COMPONENT_TYPES.has(componentType)
  );
}

export function isHomeAssistantControlComponent(componentType: ComponentType): boolean {
  return CONTROL_COMPONENT_TYPES.has(componentType);
}

export function getHomeAssistantDomain(entityId: string, explicitDomain?: string): string {
  if (explicitDomain && explicitDomain.trim()) return explicitDomain.trim();
  const [domain] = entityId.split(".");
  return domain && domain !== entityId ? domain : "sensor";
}

export function getHomeAssistantEntityLabel(entity: HAEntity): string {
  const friendlyName = entity.attributes.friendly_name;
  return typeof friendlyName === "string" && friendlyName.trim()
    ? friendlyName
    : entity.entity_id;
}

export function toHomeAssistantEntitySelection(
  entity: HAEntity,
  instanceId?: string,
  instanceName?: string,
): HomeAssistantEntitySelection {
  return {
    instanceId,
    instanceName,
    entityId: entity.entity_id,
    domain: entity.domain || getHomeAssistantDomain(entity.entity_id),
    displayName: getHomeAssistantEntityLabel(entity),
    areaName: entity.area?.name,
    state: entity.state,
    attribute: "state",
  };
}

export function extractHomeAssistantBinding(
  properties: Record<string, unknown>,
): HomeAssistantEntitySelection | null {
  const binding = isRecord(properties.binding) ? properties.binding : null;
  const dataSource = isRecord(properties.dataSource) ? properties.dataSource : null;

  const entityId =
    (typeof binding?.entityId === "string" && binding.entityId) ||
    (typeof dataSource?.entityId === "string" && dataSource.entityId) ||
    (typeof properties.entityId === "string" && properties.entityId) ||
    "";

  if (!entityId) return null;

  return {
    entityId,
    domain:
      (typeof binding?.domain === "string" && binding.domain) ||
      (typeof dataSource?.domain === "string" && dataSource.domain) ||
      getHomeAssistantDomain(entityId),
    attribute:
      (typeof binding?.attribute === "string" && binding.attribute) ||
      (typeof dataSource?.attribute === "string" && dataSource.attribute) ||
      "state",
    instanceId:
      (typeof binding?.instanceId === "string" && binding.instanceId) ||
      (typeof dataSource?.instanceId === "string" && dataSource.instanceId) ||
      undefined,
    instanceName:
      (typeof binding?.instanceName === "string" && binding.instanceName) ||
      (typeof dataSource?.instanceName === "string" && dataSource.instanceName) ||
      undefined,
    displayName:
      typeof binding?.displayName === "string" ? binding.displayName : undefined,
    areaName:
      typeof binding?.areaName === "string" ? binding.areaName : undefined,
  };
}

export function inferHomeAssistantAction(
  componentType: ComponentType,
  selection: HomeAssistantEntitySelection,
): HomeAssistantManualActionMetadata | null {
  if (!isHomeAssistantControlComponent(componentType)) return null;

  const domain = getHomeAssistantDomain(selection.entityId, selection.domain);
  const serviceSpec = inferHomeAssistantServiceSpec(componentType, domain);
  if (!serviceSpec || !isAllowedHomeAssistantService(domain, serviceSpec.service)) {
    return null;
  }

  const riskLevel = isReviewRequired(selection.entityId, domain)
    ? "review_required"
    : "low";
  const reviewRequired = riskLevel === "review_required";

  return {
    type: "home-assistant-placeholder",
    manualOnly: true,
    entityId: selection.entityId,
    domain,
    service: serviceSpec.service,
    serviceLabel: `${domain}.${serviceSpec.service}`,
    ...(serviceSpec.serviceData ? { serviceData: serviceSpec.serviceData } : {}),
    riskLevel,
    reviewRequired,
    safetyNote: reviewRequired
      ? "This live Home Assistant control is supported, but TerraMatrix requires explicit confirmation before running review-required services."
      : "TerraMatrix can run this Home Assistant service live after the component is bound to an active Home Assistant instance.",
  };
}

export function createHomeAssistantBindingUpdate(
  component: Component,
  selection: HomeAssistantEntitySelection,
): HomeAssistantBindingUpdate {
  const entityId = selection.entityId.trim();
  const domain = getHomeAssistantDomain(entityId, selection.domain);
  const selectedAttribute = selection.attribute?.trim();
  const attribute =
    domain === "light" &&
    (component.type === ComponentType.SLIDER || component.type === ComponentType.RADIAL_DIAL) &&
    (!selectedAttribute || selectedAttribute === "state")
      ? "brightness"
      : selectedAttribute || "state";
  const binding: HomeAssistantBindingMetadata = {
    source: "home-assistant",
    type: "entity-state",
    version: 2,
    entityId,
    domain,
    attribute,
    ...(selection.instanceId ? { instanceId: selection.instanceId } : {}),
    ...(selection.instanceName ? { instanceName: selection.instanceName } : {}),
    ...(selection.displayName ? { displayName: selection.displayName } : {}),
    ...(selection.areaName ? { areaName: selection.areaName } : {}),
    selectedVia: "entity-picker",
    manualReviewRequired: isReviewRequired(entityId, domain),
  };
  const dataSource: HomeAssistantDataSourceMetadata = {
    type: "home-assistant",
    mode: "ha-entity-state",
    entityId,
    domain,
    attribute,
    ...(selection.instanceId ? { instanceId: selection.instanceId } : {}),
    ...(selection.instanceName ? { instanceName: selection.instanceName } : {}),
  };
  const action = inferHomeAssistantAction(component.type, {
    ...selection,
    entityId,
    domain,
    attribute,
  });

  return {
    properties: {
      ...component.properties,
      entityId,
      dataSource,
      binding,
      queryTemplateId: "ha-current-entity-state",
      ...(action ? { action } : {}),
    },
  };
}

const isHomeAssistantPlaceholderAction = (
  action: unknown,
): action is Record<string, unknown> =>
  isRecord(action) && action.type === "home-assistant-placeholder";

const hasHomeAssistantBindingSource = (
  properties: Record<string, unknown>,
  action: Record<string, unknown>,
): boolean => {
  const binding = isRecord(properties.binding) ? properties.binding : null;
  const dataSource = isRecord(properties.dataSource) ? properties.dataSource : null;
  if (binding?.source === "home-assistant" || dataSource?.type === "home-assistant") {
    return true;
  }
  if (properties.queryTemplateId === "ha-current-entity-state") {
    return true;
  }

  const propertyEntityId =
    typeof properties.entityId === "string" ? properties.entityId : "";
  const actionEntityId = typeof action.entityId === "string" ? action.entityId : "";
  return Boolean(propertyEntityId && actionEntityId && propertyEntityId === actionEntityId);
};

const actionMatchesHomeAssistantBinding = (
  action: Record<string, unknown>,
  binding: HomeAssistantEntitySelection | null,
): boolean => {
  if (!binding) return true;

  const actionEntityId = typeof action.entityId === "string" ? action.entityId : "";
  if (actionEntityId && actionEntityId !== binding.entityId) return false;

  const actionDomain = typeof action.domain === "string" ? action.domain : "";
  if (actionDomain && binding.domain && actionDomain !== binding.domain) return false;

  return true;
};

export function shouldClearHomeAssistantPlaceholderAction(
  properties: Record<string, unknown>,
): boolean {
  const action = properties.action;
  if (!isHomeAssistantPlaceholderAction(action)) return false;
  if (!hasHomeAssistantBindingSource(properties, action)) return false;

  return actionMatchesHomeAssistantBinding(
    action,
    extractHomeAssistantBinding(properties),
  );
}

export function createHomeAssistantUnbindUpdate(component: Component): HomeAssistantBindingUpdate {
  const shouldClearAction = shouldClearHomeAssistantPlaceholderAction(component.properties);

  return {
    properties: {
      ...component.properties,
      entityId: undefined,
      dataSource: undefined,
      binding: undefined,
      queryTemplateId: undefined,
      ...(shouldClearAction ? { action: undefined } : {}),
    },
  };
}

export function describeHomeAssistantBinding(properties: Record<string, unknown>): string | null {
  const binding = extractHomeAssistantBinding(properties);
  if (!binding) return null;

  const label = binding.displayName && binding.displayName !== binding.entityId
    ? `${binding.displayName} (${binding.entityId})`
    : binding.entityId;
  const action = isRecord(properties.action) &&
    properties.action.type === "home-assistant-placeholder" &&
    typeof properties.action.serviceLabel === "string"
      ? `, manual-only action ${properties.action.serviceLabel}`
      : "";

  return `Home Assistant entity: ${label}${action}`;
}

function inferHomeAssistantServiceSpec(
  componentType: ComponentType,
  domain: string,
): InferredHomeAssistantServiceSpec | null {
  if (componentType === ComponentType.THERMOSTAT) {
    return domain === "climate"
      ? { service: "set_temperature", serviceData: { temperature: "{{value}}" } }
      : null;
  }

  if (componentType === ComponentType.SEGMENTED_CONTROL) {
    if (domain === "climate") {
      return {
        service: "set_hvac_mode",
        serviceData: { hvac_mode: "{{value}}" },
      };
    }
    return domain === "select"
      ? {
        service: "select_option",
        serviceData: { option: "{{value}}" },
      }
      : null;
  }

  if (
    componentType === ComponentType.SLIDER ||
    componentType === ComponentType.RADIAL_DIAL ||
    componentType === ComponentType.STEPPER
  ) {
    return inferRangeControlServiceSpec(domain);
  }

  if (
    componentType === ComponentType.ICON_BUTTON ||
    componentType === ComponentType.PUSH_BUTTON ||
    componentType === ComponentType.TOGGLE_SWITCH
  ) {
    return inferToggleStyleServiceSpec(componentType, domain);
  }

  return null;
}

function inferRangeControlServiceSpec(
  domain: string,
): InferredHomeAssistantServiceSpec | null {
  if (domain === "light") {
    return { service: "turn_on", serviceData: { brightness_pct: "{{value}}" } };
  }
  if (domain === "climate") {
    return { service: "set_temperature", serviceData: { temperature: "{{value}}" } };
  }
  if (domain === "cover") {
    return {
      service: "set_cover_position",
      serviceData: { position: "{{value}}" },
    };
  }
  if (domain === "fan") {
    return {
      service: "set_percentage",
      serviceData: { percentage: "{{value}}" },
    };
  }
  if (domain === "input_number" || domain === "number") {
    return { service: "set_value", serviceData: { value: "{{value}}" } };
  }
  return null;
}

function inferToggleStyleServiceSpec(
  componentType: ComponentType,
  domain: string,
): InferredHomeAssistantServiceSpec | null {
  if (domain === "button") {
    return { service: "press" };
  }
  if (
    domain === "input_boolean" ||
    domain === "light" ||
    domain === "media_player" ||
    domain === "switch"
  ) {
    return { service: "toggle" };
  }
  if (
    componentType !== ComponentType.TOGGLE_SWITCH &&
    (componentType === ComponentType.ICON_BUTTON ||
      componentType === ComponentType.PUSH_BUTTON)
  ) {
    if (domain === "cover") {
      return { service: "open_cover" };
    }
    if (domain === "lock") {
      return { service: "unlock" };
    }
  }
  return null;
}

function isAllowedHomeAssistantService(domain: string, service: string): boolean {
  if (!isHomeAssistantControlDomain(domain)) {
    return false;
  }

  const allowedServices: readonly string[] = HOME_ASSISTANT_CONTROL_ALLOWLIST[domain];
  return allowedServices.includes(service);
}

export function isHomeAssistantReviewRequired(entityId: string, domain: string): boolean {
  return REVIEW_REQUIRED_DOMAINS.has(domain) || REVIEW_REQUIRED_ENTITY_WORDS.test(entityId);
}

function isReviewRequired(entityId: string, domain: string): boolean {
  return isHomeAssistantReviewRequired(entityId, domain);
}
